import moment from 'moment-timezone';

import {
  REPORTTYPE_HEADERBIDDER_BYAUCTIONS_FILLRATE,
  REPORTTYPE_IAS_FIRST_PARTY,
  REPORTTYPE_IAS_THIRD_PARTY,
} from '~/src/dash/api/console-api';

export interface IFormatFunction {
  name: string;
  regex: RegExp;
  formattingFunction: (
    input: any,
    reportType?: string,
    exchangeRate?: number,
    currencySymbol?: string,
  ) => any;
}

// formatData formats data contained into a list of object with a set of formatting rules give by formatFunction
// For a clear use example, see report-table-view.tsx
// The graphID is currently only used for the auctions fill rate table to round to 2 decimal places
// This can be deleted later on if / when we change all of the rate metrics to 2 decimal places
export const formatData = (
  originalData: object[],
  formatFunctions: IFormatFunction[],
  reportType: string,
  exchangeRate = 1,
  currencySymbol = '$',
): object[] => {
  const formattedData: object[] = [];
  // For every row of data
  originalData.map((row: object) => {
    const values = Object.values(row);
    const names = Object.keys(row);
    const formattedRow: { [key: string]: any } = {};
    // For every column of every row
    for (let i = 0; i < values.length; i++) {
      const name = names[i];
      let value = values[i];
      // For every format function
      formatFunctions.map((formatter: IFormatFunction) => {
        if (formatter.regex.test(name)) {
          // There is a match => format the value
          if (formatter.name === 'formatRate') {
            value = formatter.formattingFunction(value, reportType);
          } else if (formatter.name === 'formatCurrency') {
            value = formatter.formattingFunction(value, '0', exchangeRate, currencySymbol);
          } else {
            value = formatter.formattingFunction(value);
          }
        }
      });
      formattedRow[name] = value;
    }
    formattedData.push(formattedRow);
  });

  return formattedData;
};

// formatSyntheticID transforms a number into "DeviceID" or "SyntheticID"
export const formatSyntheticID = (data: any): any => {
  if (typeof data !== 'number') {
    return data;
  }

  if (data === 0) {
    return 'Device ID';
  }

  if (data === 1) {
    return 'Synthetic ID';
  }

  if (data === 2) {
    return 'Unkown';
  }
  return data;
};

export const formatSpotxEndpoint = (data: number) => {
  if (data === 1) {
    return 'US West';
  } else if (data === 2) {
    return 'US East';
  } else if (data === 3) {
    return 'EMEA';
  } else if (data === 4) {
    return 'APAC';
  } else {
    return 'Other / Non-Spotx Endpoint';
  }
};

// formatSpotxChannelID transforms a number into NULL if this number == 0
export const formatSpotxChannelID = (data: any): any => {
  if (typeof data !== 'number') {
    return data;
  }

  if (data === 0) {
    return '';
  }
  return data;
};

// formatDuration transforms a number into a duration in s
// Example: 93 -> 93.00s
export const formatDuration = (data: any): any => {
  if (typeof data !== 'number') {
    return data;
  }
  return (Math.round(data * 100) / 100).toFixed(2) + 's';
};

// formatLatency transforms a number into a duration in ms
// Example: 76 -> 76.00ms
export const formatLatency = (data: any): any => {
  if (typeof data !== 'number') {
    return data;
  }
  return (Math.round(data * 100) / 100).toFixed(2) + 'ms';
};

// formatCurrency transforms a number or a string into a USD currency
// Example: 32 -> $32.00
export const formatCurrency = (
  data: any,
  _: string,
  exchangeRate = 1,
  currencySymbol = '$',
): any => {
  if (typeof data === 'string') {
    return currencySymbol + (parseFloat(data) * exchangeRate).toFixed(2);
  } else if (typeof data === 'number') {
    return currencySymbol + (Math.round(data * exchangeRate * 100) / 100).toFixed(2);
  }
  return data;
};

// formatRate transforms a number into a percentage
// Example: 0.43 -> 43%
export const formatRate = (data: any, reportType: string): any => {
  if (typeof data !== 'number') {
    return data;
  }
  if (reportType === REPORTTYPE_HEADERBIDDER_BYAUCTIONS_FILLRATE) {
    return (data * 100).toFixed(2) + '%';
  }
  if (reportType === REPORTTYPE_IAS_FIRST_PARTY || reportType === REPORTTYPE_IAS_THIRD_PARTY) {
    return (data * 100).toFixed(2) + '%';
  }
  return Math.round(data * 100) + '%';
};

export const formatRateToTwoDecimals = (rate: number): string => {
  return (rate * 100).toFixed(2) + '%';
};

// formatHugeNumber transforms a number to a comma separated number. Work also with number in strings
// Examples: 123456789 -> 123,456,789 || "I ate 47312 apples" -> "I ate 47,312 apples"
export const formatHugeNumber = (data: any): any => {
  // If data is a number, return or convert it
  if (typeof data === 'number') {
    if (data < 1000 && data > -1000) {
      return data; // Number too small to need coma separation
    }
    data = data.toString(); // We want a string to include the comma(s)
  }

  // Check if data is a string
  if (typeof data !== 'string') {
    return data;
  }

  // Formatting large number
  let counter = 0;
  for (let i = data.length - 1; i >= 0; i--) {
    if (data[i] >= '0' && data[i] <= '9') {
      if (counter > 0 && counter % 3 === 0) {
        data = data.slice(0, i + 1) + ',' + data.slice(i + 1);
      }
      counter += 1;
    } else {
      counter = 0;
    }
  }

  return data;
};

// formatDate transforms a sql-fromatted date into a hunan one
// Example: 2020-03-12 -> March 12th 2020
export const formatDate = (data: any): any => {
  // Verify type
  if (typeof data !== 'string') {
    return data;
  }

  if (!data.includes('T')) {
    data = data + 'T00:00:00+00:00';
  }

  const date = new Date(data);

  if (data.split('-').length === 2) {
    // Only the month
    return moment(date).tz('UTC').format('MMMM YYYY');
  }
  // Full date
  return moment(date).tz('UTC').format('MMMM Do YYYY');
};

// formatMonth transforms a sql-fromatted date into a hunan one (without days)
// Example: 2020-03-12 -> March 2020
export const formatMonth = (data: any): any => {
  // Verify type
  if (typeof data !== 'string') {
    return data;
  }

  if (!data.includes('T')) {
    data = data + 'T00:00:00+00:00';
  }

  const date = new Date(data);

  return moment(date).tz('UTC').format('MMMM YYYY');
};

// formatHour transforms a sql-formatted date into a hunan one (without days)
// Example: 2020-03-12T00:00:00 -> March 12th 2020  15h00
export const formatHour = (data: any): any => {
  // Verify type
  if (typeof data !== 'string') {
    return data;
  }

  let date: Date;

  // TODO REMOVE THIS WHEN ALL REPORTS ARE MIGRATED TO SNOWFLAKE (28-35-39-41)
  date = new Date(data + '+00:00');
  if (!isValidDate(date)) {
    date = new Date(data);
  }

  if (data.split('-').length === 2) {
    // Only the month
    return moment(date).tz('UTC').format('MMMM YYYY');
  }
  // Full date
  return moment(date).tz('UTC').format('MMMM Do YYYY HH:mm');
};

export const formatForClassname = (name: string) => {
  if (name === '') {
    return name;
  }

  return name
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/\s+/g, '-')
    .replace(/\[\d+\]-/g, '')
    .toLowerCase();
};

export const formatAdBreakPosition = (data: number): string => {
  if (data === 0) {
    return 'No Position Specified';
  } else if (data === 1) {
    return 'Pre-Roll';
  } else if (data === 2) {
    return 'Mid-Roll';
  } else if (data === 3) {
    return 'Post-Roll';
  } else {
    return 'Unknown Position';
  }
};

export const formatIsPartialAdBreak = (data: boolean): string => {
  if (data) {
    return 'Partial Ad Break';
  }
  return 'Full Ad Break';
};

export const formatBoolean = (data: boolean): string => {
  if (data) {
    return 'TRUE';
  }
  return 'FALSE';
};

export const removeIDForClassname = (name: string) => {
  if (name === '') {
    return name;
  }

  const nameParts = name.split(' ');
  if (nameParts.length === 2) {
    return nameParts[1];
  }

  return name;
};

function isValidDate(d: any) {
  return d instanceof Date && !isNaN(d as any);
}

const OneBillion = 1000000000;
const OneMillion = 1000000;
const OneThousand = 1000;

// formats million and billion numbers to 1.30M or 200.30B
export const formatHugeNumberToShortenedString = (num: number): string => {
  if (num >= OneBillion) {
    return (num / OneBillion).toFixed(2).toLocaleString() + 'B';
  } else if (num >= OneMillion) {
    return (num / OneMillion).toFixed(2).toLocaleString() + 'M';
  }
  return num.toLocaleString();
};

// for yAxis on graphs, abbreviate the number ticker
export const formatHugeNumberToShortenedStringV2 = (value: number): string => {
  if (value > OneBillion) {
    return (value / OneBillion).toString() + 'B';
  } else if (value > OneMillion) {
    return (value / OneMillion).toString() + 'M';
  } else if (value > OneThousand) {
    return (value / OneThousand).toString() + 'K';
  }
  return value.toString();
};

export const formatMediaTypeID = (data: string): string => {
  switch (data) {
    case '111':
      return 'Desktop Web Display';
    case '112':
      return 'Desktop Web Video';
    case '121':
      return 'Mobile Web Display';
    case '122':
      return 'Mobile Web Video';
    case '131':
      return 'Tablet Web Display';
    case '132':
      return 'Table Web Video';
    case '221':
      return 'Mobile In-App Display';
    case '222':
      return 'Mobile In-App Video';
    case '231':
      return 'Tablet In-App Display';
    case '252':
      return 'CTV In App Video';
    case '152':
      return 'Browser CTV Video';
    default:
      return '';
  }
};

interface IDisplayLimitedItemsOptions {
  items: string[];
  limit?: number;
  joinString?: string;
  preNumString?: string;
  postNumString?: string;
}

export const displayLimitedItems = ({
  items,
  limit = 2,
  joinString = ', ',
  preNumString = ', +',
  postNumString = ' more',
}: IDisplayLimitedItemsOptions): string => {
  if (limit < 1) {
    limit = 1;
  }
  if (items.length <= limit) {
    return items.join(joinString);
  }
  // default return example: 'Steph, Klay, +3 more'
  return (
    items.slice(0, limit).join(joinString) + preNumString + (items.length - limit) + postNumString
  );
};

// Function to extract domain from URL, removing protocol, path, and query parameters
export const extractDomainFromUrl = (url: string): string => {
  if (!url) return '';

  try {
    // Remove protocol if present
    let domain = url.replace(/^https?:\/\//, '');

    // Remove query parameters and fragments
    domain = domain.split('?')[0].split('#')[0];

    // Remove path (everything after the first slash)
    domain = domain.split('/')[0];

    // Remove www. prefix if present
    domain = domain.replace(/^www\./, '');

    return domain;
  } catch (error) {
    // If parsing fails, return the original string
    return url;
  }
};
